import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';

export interface BaseTag {
  name: string;
  color: string;
}

export interface Tag extends BaseTag {
  id: number | string;
  contractCount?: number;
  createdBy?: string;
  usage_count?: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  client_id?: number;
  is_active?: boolean;
  isNew?: boolean;
  isEditing?: boolean;
}

export interface ContractTag extends BaseTag {
  contractId: number;
  tagId: number;
}

export interface CreateTagPayload {
  name: string;
  color?: string;
  client_id?: number;
}

export interface UpdateTagPayload {
  name?: string;
  color?: string;
  is_active?: boolean;
}

/**
 * Custom hook that provides API functions for managing tags.
 * @returns {object} An object containing all tag management API functions
 */
export const useTagsApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  /**
   * Retrieves all tags from the API.
   * @param {number} [profileId] - Optional profile ID to filter tags by profile
   * @returns {Promise<Tag[] | undefined>} Promise that resolves to an array of tags, or undefined if an error occurs
   */
  const getAllTags = async (profileId?: number): Promise<Tag[] | undefined> => {
    try {
      const response = await axios.get(`/tags`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to fetch tags'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates a new tag.
   * @param {CreateTagPayload} tagData - The tag data to create
   * @param {number} [profileId] - Optional profile ID
   * @returns {Promise<Tag | undefined>} Promise that resolves to the created tag, or undefined if an error occurs
   */
  const createTag = async (tagData: CreateTagPayload, profileId?: number): Promise<Tag | undefined> => {
    try {
      const response = await axios.post(`/tags`, tagData, {
        params: { profileId }
      });
      if (response?.status === 201 || response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create tag'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Updates an existing tag.
   * @param {number} tagId - The ID of the tag to update
   * @param {UpdateTagPayload} updates - The updates to apply to the tag
   * @param {number} [profileId] - Optional profile ID
   * @returns {Promise<Tag | undefined>} Promise that resolves to the updated tag, or undefined if an error occurs
   */
  const updateTag = async (tagId: number, updates: UpdateTagPayload, profileId?: number): Promise<Tag | undefined> => {
    try {
      const response = await axios.patch(`/tags/${tagId}`, updates, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to update tag'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Deletes a tag.
   * @param {number} tagId - The ID of the tag to delete
   * @param {number} [profileId] - Optional profile ID
   * @returns {Promise<boolean | undefined>} Promise that resolves to true if successful, or undefined if an error occurs
   */
  const deleteTag = async (tagId: number, profileId?: number): Promise<boolean | undefined> => {
    try {
      const response = await axios.delete(`/tags/${tagId}`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return true;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to delete tag'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Reads the client's tag-creation setting. Best-effort: on any failure it resolves to `false`
   * rather than surfacing an error toast, since this only drives whether the Tags field offers
   * tag creation. Reads a client-level flag with no current-contract dependency.
   * @param {number} [profileId] Optional profile ID
   * @returns {Promise<boolean>} Whether users may create tags for this client
   */
  const getTagSettings = async (profileId?: number): Promise<boolean> => {
    try {
      const response = await axios.get(`/account/tags/settings`, { params: { profileId } });
      return Boolean(response?.data?.allowUserTagCreation);
    } catch {
      return false;
    }
  };

  /**
   * Updates tag-related account settings
   * @param {boolean} allowUserTagCreation Whether users are allowed to create tags
   * @param {number} [profileId] Optional profile ID
   * @returns {Promise<any | undefined>} Settings update response or undefined if an error occurs
   */
  const updateTagSettings = async (allowUserTagCreation: boolean, profileId?: number): Promise<any | undefined> => {
    try {
      const response = await axios.patch(`/account/tags/settings`, { allowUserTagCreation }, { params: { profileId } });
      if (response?.status === 200) {
        return response.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to update tag settings'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  return {
    getAllTags,
    createTag,
    updateTag,
    deleteTag,
    getTagSettings,
    updateTagSettings
  };
};
